/**
 * ContactPage - formul\u00e1rio de contacto + cart\u00f5es de contacto din\u00e2micos
 */
import { useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Send, MessageCircle, Briefcase, LifeBuoy } from "lucide-react";
import { api, getApiErrorMessage } from "@/lib/api";
import { usePublicSettings } from "@/lib/publicSettings";

export default function ContactPage() {
    const settings = usePublicSettings();
    const company = settings?.company || {};
    const contacts = settings?.contacts || {};
    const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact/message", form);
            toast.success("Mensagem enviada com sucesso!", {
                description: "Entraremos em contacto em breve.",
            });
            setForm({ name: "", email: "", company: "", phone: "", message: "" });
        } catch (err) {
            toast.error(getApiErrorMessage(err, "N\u00e3o foi poss\u00edvel enviar."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="py-14 sm:py-20 bg-hero-radial-light" data-testid="contact-page">
            <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
                <div>
                    <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
                        Vamos conversar.
                    </h1>
                    <p className="mt-3 text-muted-foreground max-w-md">
                        Tem d\u00favidas, sugest\u00f5es ou quer conhecer melhor a MeSafa? Envie uma mensagem ou contacte-nos diretamente.
                    </p>
                    <div className="mt-8 grid sm:grid-cols-2 gap-3">
                        <ContactCard
                            icon={Mail}
                            title="Geral"
                            primary={contacts.general_email}
                            secondary={contacts.general_phone}
                        />
                        <ContactCard
                            icon={LifeBuoy}
                            title="Suporte t\u00e9cnico"
                            primary={contacts.support_email}
                            secondary={contacts.support_whatsapp ? `WhatsApp: ${contacts.support_whatsapp}` : null}
                        />
                        <ContactCard
                            icon={Briefcase}
                            title="Neg\u00f3cios"
                            primary={contacts.business_email}
                            secondary={contacts.business_phone}
                        />
                        <ContactCard
                            icon={MapPin}
                            title="Morada"
                            primary={company.address}
                            secondary={company.city && company.country ? `${company.city}, ${company.country}` : null}
                        />
                    </div>
                </div>
                <Card className="p-6 sm:p-8 rounded-2xl bg-card border-border">
                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="name">Nome *</Label>
                                <Input
                                    id="name"
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="contact-name-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="email">Email *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    data-testid="contact-email-input"
                                />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="company">Empresa</Label>
                                <Input
                                    id="company"
                                    value={form.company}
                                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                                    data-testid="contact-company-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="phone">Telefone</Label>
                                <Input
                                    id="phone"
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    data-testid="contact-phone-input"
                                />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="message">Mensagem *</Label>
                            <Textarea
                                id="message"
                                required
                                rows={5}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                data-testid="contact-message-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full btn-grad-purple text-white h-11"
                            data-testid="contact-submit-button"
                        >
                            {loading ? "A enviar\u2026" : (
                                <>
                                    <Send className="h-4 w-4 mr-2" /> Enviar mensagem
                                </>
                            )}
                        </Button>
                    </form>
                </Card>
            </div>
        </div>
    );
}

function ContactCard({ icon: Icon, title, primary, secondary }) {
    if (!primary && !secondary) return null;
    return (
        <Card className="p-4 rounded-2xl bg-card border-border">
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-[#6B21D9]/10 grid place-items-center text-[#6B21D9] shrink-0">
                    <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">{title}</div>
                    {primary && <div className="font-medium text-sm break-words">{primary}</div>}
                    {secondary && <div className="text-xs text-muted-foreground break-words">{secondary}</div>}
                </div>
            </div>
        </Card>
    );
}
